import React, { FC, useMemo } from "react";
import { Link } from "react-router-dom";

import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import {
  buildWorkload,
  formatHours,
  isOverdue,
  OPEN_GROUPS,
  personWorkLink,
  STATUS_COLORS,
  statusGroup,
} from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { PeopleClientTypeFilters, useReportFilters } from "../ui/ReportFilters";
import {
  Avatar,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  Legend,
  LINK,
  LoadError,
  Loading,
  Note,
  NUM,
  ReportPage,
  StackedBar,
  statusLegend,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllWorks } from "../useReportData";

/** Team member name: opens their work on the Work page (QA batch 12). */
const PersonName: FC<{
  id?: string | null;
  name: string;
  access: { firmWide: boolean; userId?: string | null };
}> = ({ id, name, access }) => {
  const link = personWorkLink(id, access);
  return link ? (
    <Link to={link} className={LINK}>
      {name}
    </Link>
  ) : (
    <>{name}</>
  );
};

export const WorkloadReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const works = useAllWorks({
    hideGreenStatus: true,
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    worktypeIds: filters.workTypeIds,
  });

  const rows = useMemo(() => buildWorkload(works.data), [works.data]);
  const overdue = works.data.filter((w) => isOverdue(w)).length;
  const hoursLeft = works.data.reduce(
    (sum, w) =>
      sum + Math.max(0, (w.budgetedTime || 0) - (w.totalDuration || 0) / 3600),
    0,
  );
  const max = Math.max(1, ...rows.map((r) => r.total));
  const totals = OPEN_GROUPS.map(
    (g) => works.data.filter((w) => statusGroup(w) === g).length,
  );

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        "Team member": r.name,
        Ready: r.counts.Ready,
        "In progress": r.counts["In Progress"],
        Waiting: r.counts.Waiting,
        Scheduled: r.counts.Scheduled,
        "Total open": r.total,
        Overdue: r.overdue,
        "Budget hours left": Math.round(r.hoursLeft * 10) / 10,
      })),
      "Team workload",
    );

  const tableName = "Open work by team member";
  const shownRows = useFocusRows(tableName, rows, (r) => r.id || "none");

  const ask = useMemo(
    () => ({
      title: "Team workload",
      path: "/reports/workload",
      description:
        "Open work for each team member, by status. Work with two assignees counts for both.",
      filters: filtersText([!access.firmWide && "Only your own work"]),
      kpis: [
        { label: "Open work", value: String(works.data.length) },
        { label: "Overdue", value: String(overdue) },
        {
          label: "Team members",
          value: String(rows.filter((r) => r.id).length),
        },
        { label: "Budget hours left", value: formatHours(hoursLeft) },
      ],
      tables: [
        askTable(
          tableName,
          rows,
          [
            {
              key: "name",
              label: "Team member",
              kind: "text",
              value: (r) => r.name,
            },
            {
              key: "ready",
              label: "Ready",
              kind: "number",
              value: (r) => r.counts.Ready,
            },
            {
              key: "inProgress",
              label: "In progress",
              kind: "number",
              value: (r) => r.counts["In Progress"],
            },
            {
              key: "waiting",
              label: "Waiting",
              kind: "number",
              value: (r) => r.counts.Waiting,
            },
            {
              key: "scheduled",
              label: "Scheduled",
              kind: "number",
              value: (r) => r.counts.Scheduled,
            },
            {
              key: "total",
              label: "Total open",
              kind: "number",
              value: (r) => r.total,
            },
            {
              key: "overdue",
              label: "Overdue",
              kind: "number",
              value: (r) => r.overdue,
            },
            {
              key: "hoursLeft",
              label: "Budget hours left",
              kind: "hours",
              value: (r) => r.hoursLeft,
            },
          ],
          (r) => r.id || "none",
          (r) => personWorkLink(r.id, access),
        ),
        askTable(
          "Total work items by status",
          OPEN_GROUPS.map((g, i) => ({ group: g, count: totals[i] })),
          [
            {
              key: "status",
              label: "Status",
              kind: "text",
              value: (r) => r.group,
            },
            {
              key: "count",
              label: "Work items",
              kind: "number",
              value: (r) => r.count,
            },
          ],
          (r) => r.group,
        ),
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, works.data, access.firmWide, access.userId],
  );

  return (
    <ReportPage
      title={"Team workload"}
      description={
        "Open work for each team member, by status. Work with two assignees counts for both."
      }
      onExport={rows.length ? onExport : undefined}
      ask={works.isLoading ? undefined : ask}
      askSuggestions={[
        "Who has the most overdue work?",
        "Who has room for more work?",
        "Who has the most budget hours left?",
      ]}
      filters={
        <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
      }>
      {works.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Open work"}
          value={works.isLoading ? "…" : works.data.length}
          note={"Not completed"}
        />
        <Kpi
          label={"Overdue"}
          value={works.isLoading ? "…" : overdue}
          note={"Past the due date"}
          tone={"red"}
        />
        <Kpi
          label={"Team members"}
          value={works.isLoading ? "…" : rows.filter((r) => r.id).length}
          note={"With open work"}
        />
        <Kpi
          label={"Budget hours left"}
          value={works.isLoading ? "…" : formatHours(hoursLeft)}
          note={"On open work"}
        />
      </KpiGrid>

      <Card
        title={"Open work by team member"}
        right={<Legend items={statusLegend(OPEN_GROUPS)} />}>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !rows.length ? (
          <Empty text={"No open work."} />
        ) : (
          <div className={"flex flex-col gap-3"}>
            {shownRows.map((row) => (
              <div
                key={row.id || "none"}
                className={"grid items-center gap-3.5"}
                style={{
                  gridTemplateColumns:
                    "minmax(120px, 190px) minmax(0, 1fr) 80px",
                }}>
                <div className={"truncate text-[13.5px] font-bold"}>
                  <Avatar name={row.name} color={row.color} />
                  <PersonName id={row.id} name={row.name} access={access} />
                </div>
                <StackedBar
                  max={max}
                  parts={OPEN_GROUPS.map((g) => ({
                    value: row.counts[g],
                    color: STATUS_COLORS[g],
                    label: g,
                  }))}
                />
                <div className={"text-right text-[13px] font-bold"}>
                  {row.total} open
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={"Details"}>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !rows.length ? (
          <Empty text={"No open work."} />
        ) : (
          <TableBox>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Team member
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Ready
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  In progress
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Waiting
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Scheduled
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Total open
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Overdue
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Budget hours left
                </th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map((row) => (
                <tr key={row.id || "none"}>
                  <td className={TD}>
                    <Avatar name={row.name} color={row.color} />
                    <span className={"font-bold"}>
                      <PersonName id={row.id} name={row.name} access={access} />
                    </span>
                  </td>
                  {OPEN_GROUPS.map((g) => (
                    <td key={g} className={`${TD} ${NUM}`}>
                      {row.counts[g]}
                    </td>
                  ))}
                  <td className={`${TD} ${NUM} font-extrabold`}>{row.total}</td>
                  <td
                    className={`${TD} ${NUM} ${
                      row.overdue ? "font-extrabold text-[#B42318]" : ""
                    }`}>
                    {row.overdue}
                  </td>
                  <td className={`${TD} ${NUM}`}>
                    {formatHours(row.hoursLeft)}
                  </td>
                </tr>
              ))}
              <tr>
                <td className={`${TD} font-extrabold`}>Total work items</td>
                {totals.map((t, i) => (
                  <td
                    key={OPEN_GROUPS[i]}
                    className={`${TD} ${NUM} font-extrabold`}>
                    {t}
                  </td>
                ))}
                <td className={`${TD} ${NUM} font-extrabold`}>
                  {works.data.length}
                </td>
                <td className={`${TD} ${NUM} font-extrabold text-[#B42318]`}>
                  {overdue}
                </td>
                <td className={`${TD} ${NUM} font-extrabold`}>
                  {formatHours(hoursLeft)}
                </td>
              </tr>
            </tbody>
          </TableBox>
        )}
      </Card>
      <Note>
        &quot;Not assigned (role only)&quot; is work that is assigned to a role
        and has no team member on it. When you filter by a team member, the
        cards also count work they picked up for a role, so the cards can be
        higher than that person&apos;s bar.
      </Note>
    </ReportPage>
  );
};
